import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Share, SquarePlus } from "lucide-react";
import { getBrandingForSlug } from "@/lib/branding";
import {
  ANDROID_NATIVE_PROMPT_WAIT_MS,
  pickInstallMode,
  readMemory,
  shouldOfferInstall,
  slugFromPath,
  writeMemory,
  type InstallMode,
  type InstallPlatform,
} from "@/lib/install-prompt";
import { isValidSlug } from "@/lib/pwa-manifest";
import {
  getDeferredInstallPrompt,
  getDeviceInfo,
  isInIframe,
  isPreviewSession,
  isStandalone,
  notifyInstallStateChanged,
  onOpenInstallPromptRequest,
  safeLocalStorage,
  subscribeInstallState,
  syncManifestLink,
  takeDeferredInstallPrompt,
} from "@/lib/pwa";
import { useModalDialog } from "./useModalDialog";

/** Respiro entre a página abrir e o convite subir. */
export const INSTALL_PROMPT_SHOW_DELAY_MS = 1200;

interface InstallAppPromptProps {
  showDelayMs?: number;
  nativeWaitMs?: number;
}

type CloseMemory = "snooze" | "installed" | null;

/**
 * Convite "Instale o app" do portal do aluno. Sobe sozinho logo na entrada
 * (inclusive na tela de login — no iPhone o app instalado tem login próprio,
 * então instalar antes de entrar poupa um login), mas nunca por cima de quem
 * está digitando; pode ser reaberto pelo Perfil. Quem decide QUANDO:
 * install-prompt.ts; o instalador nativo e o manifesto: pwa.ts.
 */
export function InstallAppPrompt({
  showDelayMs = INSTALL_PROMPT_SHOW_DELAY_MS,
  nativeWaitMs = ANDROID_NATIVE_PROMPT_WAIT_MS,
}: InstallAppPromptProps) {
  const { pathname, search } = useLocation();
  const rawSlug = slugFromPath(pathname);
  const slug = rawSlug && isValidSlug(rawSlug) ? rawSlug : null;

  const branding = useQuery({
    queryKey: ["branding", slug],
    queryFn: () => getBrandingForSlug(slug ?? ""),
    enabled: slug !== null,
    staleTime: 5 * 60_000,
  });

  const nativePrompt = useSyncExternalStore(subscribeInstallState, getDeferredInstallPrompt, () => null);
  const typing = useUserIsTyping();
  const [device] = useState(getDeviceInfo);
  const [delayDone, setDelayDone] = useState(false);
  const [nativeWaitElapsed, setNativeWaitElapsed] = useState(false);
  const [closed, setClosed] = useState(false);
  const [forced, setForced] = useState(false);
  const [busy, setBusy] = useState(false);
  // Enquanto o instalador nativo está aberto o evento some da store; o sheet não pode trocar de cara.
  const [frozenMode, setFrozenMode] = useState<InstallMode | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    syncManifestLink(pathname);
  }, [pathname]);

  useEffect(() => {
    const showTimer = window.setTimeout(() => setDelayDone(true), showDelayMs);
    const nativeTimer = window.setTimeout(() => setNativeWaitElapsed(true), nativeWaitMs);
    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(nativeTimer);
    };
  }, [showDelayMs, nativeWaitMs]);

  useEffect(
    () =>
      onOpenInstallPromptRequest(() => {
        setForced(true);
        setClosed(false);
      }),
    [],
  );

  const preview = useMemo(() => isPreviewSession(pathname, search), [pathname, search]);

  const mode =
    frozenMode ??
    pickInstallMode({
      platform: device.platform,
      inAppBrowser: device.inAppBrowser,
      nativeAvailable: nativePrompt !== null,
      // Aberto pelo aluno (Perfil): sem esperar o instalador nativo.
      nativeWaitElapsed: nativeWaitElapsed || forced,
    });

  const eligible =
    slug !== null &&
    (forced || branding.data != null) &&
    shouldOfferInstall({
      standalone: isStandalone(),
      inIframe: isInIframe(),
      preview,
      memory: readMemory(safeLocalStorage(), slug),
      now: Date.now(),
      forced,
    });

  const visible = !closed && eligible && mode !== "none" && (forced || (delayDone && !typing));

  const close = useCallback(
    (memory: CloseMemory) => {
      if (slug && memory === "snooze") writeMemory(safeLocalStorage(), slug, { dismissedAt: Date.now() });
      if (slug && memory === "installed") writeMemory(safeLocalStorage(), slug, { installed: true });
      if (memory) notifyInstallStateChanged();
      setClosed(true);
      setForced(false);
      setFrozenMode(null);
      setCopied(false);
      setCopyFailed(false);
    },
    [slug],
  );

  const snooze = useCallback(() => close("snooze"), [close]);
  useModalDialog(dialogRef, visible, snooze);

  const installNative = useCallback(async () => {
    const prompt = getDeferredInstallPrompt();
    if (!prompt) {
      close(null);
      return;
    }
    setFrozenMode("native");
    setBusy(true);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      close(choice.outcome === "accepted" ? "installed" : "snooze");
    } catch (err) {
      console.warn("[pwa] instalador nativo falhou", err);
      close(null);
    } finally {
      takeDeferredInstallPrompt(); // o evento só serve uma vez
      setBusy(false);
    }
  }, [close]);

  const portalUrl = `${window.location.origin}/s/${slug ?? ""}`;
  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      // Navegador de app costuma bloquear a área de transferência: o link fica visível pra copiar à mão.
      setCopyFailed(true);
    }
  }, [portalUrl]);

  if (!visible) return null;

  const trainerName = branding.data?.trainer.full_name?.trim() || null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-app-title"
      aria-describedby="install-app-desc"
      tabIndex={-1}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) close("snooze");
      }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] outline-none backdrop-blur-sm sm:items-center"
    >
      <div className="animate-fade-in w-full max-w-sm rounded-2xl border border-border-subtle bg-surface/95 p-5 shadow-2xl backdrop-blur-xl">
        <div className="flex items-center gap-4">
          <img
            src="/icons/icon-192.png"
            alt=""
            width={56}
            height={56}
            className="h-14 w-14 shrink-0 rounded-2xl ring-1 ring-border-strong"
          />
          <h2 id="install-app-title" className="font-display text-lg leading-snug text-ink">
            Instale o app dos seus treinos
          </h2>
        </div>

        <p id="install-app-desc" className="mt-3 text-sm leading-relaxed text-muted">
          {introText(trainerName, device.platform)}
        </p>

        <ModeBody mode={mode} platform={device.platform} portalUrl={portalUrl} />

        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={() => close("snooze")}
            disabled={busy}
            className="flex-1 rounded-xl border border-border-subtle bg-elevated px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-border-strong hover:bg-surface-hover disabled:opacity-50"
          >
            Agora não
          </button>
          <PrimaryAction
            mode={mode}
            busy={busy}
            copied={copied}
            copyFailed={copyFailed}
            onInstall={installNative}
            onInstalled={() => close("installed")}
            onCopy={copyLink}
          />
        </div>
      </div>
    </div>
  );
}

const NON_TEXT_INPUTS = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "range",
  "color",
  "file",
  "image",
  "hidden",
]);

function isEditableElement(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA") return true;
  return el.tagName === "INPUT" && !NON_TEXT_INPUTS.has((el as HTMLInputElement).type);
}

/** O aluno está com o cursor num campo (ex.: e-mail do login)? O convite espera. */
function useUserIsTyping(): boolean {
  const [typing, setTyping] = useState(() => isEditableElement(document.activeElement));
  useEffect(() => {
    let timer: number | undefined;
    const update = () => setTyping(isEditableElement(document.activeElement));
    // No focusout o próximo elemento ainda não recebeu o foco: confere no tick seguinte.
    const onFocusOut = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(update, 0);
    };
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  return typing;
}

function introText(trainerName: string | null, platform: InstallPlatform): string {
  const what = trainerName ? `Seus treinos e dietas com ${trainerName}` : "Seus treinos e dietas";
  return platform === "desktop"
    ? `${what} a um clique, numa janela própria — sem precisar procurar o link.`
    : `${what} a um toque, direto da tela inicial do celular — sem precisar procurar o link.`;
}

function ModeBody({ mode, platform, portalUrl }: { mode: InstallMode; platform: InstallPlatform; portalUrl: string }) {
  if (mode === "ios") {
    return (
      <>
        <Steps
          items={[
            <>
              Toque em <strong>Compartilhar</strong> <InlineIcon icon={<Share size={15} />} /> na barra do
              navegador (se não aparecer, ele fica dentro do menu <strong>⋯</strong>)
            </>,
            <>
              Escolha <strong>Adicionar à Tela de Início</strong> <InlineIcon icon={<SquarePlus size={15} />} /> —
              role a lista se precisar
            </>,
            <>
              Toque em <strong>Adicionar</strong>. Pronto: o app aparece na sua tela inicial
            </>,
          ]}
        />
        <p className="mt-3 text-xs text-faint">Não achou a opção? Abra este link no Safari e tente de novo.</p>
      </>
    );
  }
  if (mode === "android-manual") {
    return (
      <>
        <Steps
          items={[
            <>
              Toque no menu <strong>⋮</strong> do navegador, no canto de cima
            </>,
            <>
              Escolha <strong>Instalar app</strong> ou <strong>Adicionar à tela inicial</strong>
            </>,
            <>
              Confirme em <strong>Instalar</strong>
            </>,
          ]}
        />
        <p className="mt-3 text-xs text-faint">Se no menu aparecer “Abrir no Chrome”, toque nele antes.</p>
      </>
    );
  }
  if (mode === "in-app") {
    const browser = platform === "ios" ? "Safari" : "Chrome";
    return (
      <>
        <p className="mt-4 rounded-xl border border-border-subtle bg-elevated p-3 text-sm leading-relaxed text-ink">
          Você abriu o link por dentro de outro app. Pra instalar, abra no <strong>{browser}</strong>: toque no
          menu <strong>⋯</strong> e escolha <strong>Abrir no navegador</strong> — ou copie o link e cole lá.
        </p>
        <p className="mt-2 select-all break-all rounded-lg bg-canvas px-3 py-2 font-mono text-xs text-muted">
          {portalUrl}
        </p>
      </>
    );
  }
  return null;
}

function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="mt-4 space-y-3">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-3 text-sm text-ink">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-elevated text-xs font-semibold text-accent-soft">
            {index + 1}
          </span>
          <span className="pt-0.5 leading-relaxed">{item}</span>
        </li>
      ))}
    </ol>
  );
}

function InlineIcon({ icon }: { icon: ReactNode }) {
  return (
    <span aria-hidden className="mx-0.5 inline-flex -translate-y-px align-middle text-accent-soft">
      {icon}
    </span>
  );
}

interface PrimaryActionProps {
  mode: InstallMode;
  busy: boolean;
  copied: boolean;
  copyFailed: boolean;
  onInstall: () => void;
  onInstalled: () => void;
  onCopy: () => void;
}

const PRIMARY_BUTTON =
  "inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-accent px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-accent-hover disabled:opacity-60";

function PrimaryAction({ mode, busy, copied, copyFailed, onInstall, onInstalled, onCopy }: PrimaryActionProps) {
  if (mode === "native") {
    return (
      <button type="button" onClick={onInstall} disabled={busy} className={PRIMARY_BUTTON}>
        {busy ? "Instalando…" : "Instalar app"}
      </button>
    );
  }
  if (mode === "in-app") {
    const label = copied ? "Link copiado" : copyFailed ? "Copie o link acima" : "Copiar link";
    return (
      <button type="button" onClick={onCopy} className={PRIMARY_BUTTON}>
        {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
        {label}
      </button>
    );
  }
  return (
    <button type="button" onClick={onInstalled} className={PRIMARY_BUTTON}>
      Já instalei
    </button>
  );
}
